import { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import {
  Dashboard,
  Business,
  ShoppingCart,
  AccountBalance,
  People,
  BarChart,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Settings,
  AccountTree,
  Inventory2,
  Savings,
  MoreHoriz,
  BalanceOutlined,
  CategoryOutlined,
  LabelOutlined,
  Assignment,
  LocalShipping,
  AssignmentReturn,
  Summarize,
  ReceiptLong,
  PersonSearch,
  ViewList,
  RequestQuote,
  ShoppingBag,
  Undo,
  BarChartOutlined,
  GroupWork,
  PeopleAlt,
  ListAlt,
  TableChart,
  MoveToInbox,
  Outbox,
  CompareArrows,
  Timeline,
  Receipt,
} from "@mui/icons-material";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

const HorizontalNavbar = () => {
  const history = useHistory();
  const location = useLocation();
  const { permissions, isSuperAdmin, username } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [nestedAnchorEl, setNestedAnchorEl] = useState(null);
  const [activeNestedMenu, setActiveNestedMenu] = useState(null);

  // SuperAdmin or privileged username bypasses all permission checks
  const hasFullAccess = isSuperAdmin || username === 'Baiju_Ta';

  // A section is accessible if the user has ANY claim for it (Read/Create/Edit/Delete/Cancel)
  const hasMasterAccess = hasFullAccess ||
    permissions.some(p => p.endsWith(' Master'));
  const hasTransactionAccess = hasFullAccess ||
    permissions.some(p => p.endsWith(' Transaction'));
  const hasAccountsAccess = hasFullAccess ||
    permissions.some(p => p.endsWith(' Accounts'));
  const hasAdministrationAccess = hasFullAccess ||
    permissions.some(p => p.endsWith(' Administration'));
  const hasReportsAccess = hasFullAccess ||
    permissions.some(p => p.endsWith(' Reports'));
  const hasAdminReportsAccess = hasFullAccess ||
    permissions.includes('UserRead AdminReports');

  const handleMenuOpen = (event, menuId) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
    setNestedAnchorEl(null);
    setActiveNestedMenu(null);
  };

  const handleNestedMenuOpen = (event, menuLabel) => {
    event.stopPropagation();
    // Always set the new anchor and menu - this handles switching between submenus
    setNestedAnchorEl(event.currentTarget);
    setActiveNestedMenu(menuLabel);
  };

  const handleNestedMenuClose = () => {
    setNestedAnchorEl(null);
    setActiveNestedMenu(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMenuClose();
  };

  // Check if a menu item is active based on current route
  const isMenuActive = (item) => {
    const path = location.pathname;
    if (item.route) return path === item.route || path.startsWith(item.route);
    if (item.subItems) {
      return item.subItems.some(sub => {
        if (sub.route) return path === sub.route || path.startsWith(sub.route);
        if (sub.subItems) return sub.subItems.some(s => path === s.route || path.startsWith(s.route));
        return false;
      });
    }
    return false;
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Dashboard sx={{ fontSize: 18 }} />,
      route: '/Home',
      permission: true,
    },
    {
      id: 'master',
      label: 'Master',
      icon: <Business sx={{ fontSize: 18 }} />,
      permission: hasMasterAccess,
      subItems: [
        { label: 'Board', route: '/BoardIndex' },
        { label: 'Frame', route: '/FrameIndex' },
        { label: 'Glass', route: '/GlassIndex' },
        { label: 'Hook', route: '/HookIndex' },
        { label: 'Item', route: '/ItemIndex' },
        { label: 'Lamination', route: '/LaminationIndex' },
        { label: 'Mount', route: '/MountIndex' },
        { label: 'Print', route: '/PrintIndex' },
        { label: 'Size', route: '/SizeIndex' },
        { label: 'Stand', route: '/StandIndex' },
        { label: 'Tape', route: '/TapeIndex' },
        { label: 'Master', route: '/MasterIndex' },
        { label: 'Company', route: '/CompanyEdit' },
      ],
    },
    {
      id: 'transactions',
      label: 'Transactions',
      icon: <ReceiptLong sx={{ fontSize: 18 }} />,
      permission: hasTransactionAccess,
      subItems: [
        { label: 'Estimate', route: '/EstimateIndex' },
        { label: 'Bill', route: '/BillIndex' },
        { label: 'Purchase', route: '/PurchaseIndex' },
      ],
    },
    {
      id: 'accounts',
      label: 'Accounts',
      icon: <AccountBalance sx={{ fontSize: 18 }} />,
      permission: hasAccountsAccess,
      subItems: [
        { label: 'Account Head', route: '/AccHeadIndex' },
        { label: 'Bank', route: '/BankIndex' },
        { label: 'Payment & Receipts', route: '/PaymentAndReceiptIndex' },
        { label: 'Petty Cash', route: '/PettyCashIndex' },
        { label: 'Credit / Debit', route: '/CreditDebitIndex' },
      ],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: <BarChart sx={{ fontSize: 18 }} />,
      permission: hasReportsAccess,
      subItems: [
        { label: 'Work Order Details', route: '/WorkOrderReport' },
        { label: 'Work Order Items', route: '/WorkOrderItemsReport' },
        { label: 'Bill Statement', route: '/BillStatementReport' },
        { label: 'Petty Cash',  route: '/PettyCashReport' },
        // { label: 'Ledger', route: '/Ledger' },
        // { label: 'Cash Book', route: '/CashBook' },
        // { label: 'Debtors List', route: '/DebtorsList' },
        // { label: 'Creditors List', route: '/CreditorsList' },
        // { label: 'Trial Balance',  route: '/TrialBalance' },        
      ],
    },
    {
      id: 'adminReports',
      label: 'Admin Report',
      icon: <AssuredWorkloadIcon sx={{ fontSize: 18 }} />,
      permission: hasAdminReportsAccess,
      subItems: [
        { label: 'Ledger', route: '/AdminLedger' },
        { label: 'Cash Book', route: '/AdminCashBook' },
        { label: 'Debtors List', route: '/AdminDebtorsList' },
        { label: 'Creditors List', route: '/AdminCreditorsList' },
        { label: 'Trial Balance', route: '/AdminTrialBalance' },
      ],
    },
    {
      id: 'auth',
      label: 'Auth',
      icon: <People sx={{ fontSize: 18 }} />,
      permission: hasAdministrationAccess,
      subItems: [
        { label: 'Users', route: '/UserIndex' },
        { label: 'Roles', route: '/RoleIndex' },
        { label: 'Reset Password', route: '/PasswordChange' },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings sx={{ fontSize: 18 }} />,
      permission: true,
      subItems: [
        { label: 'Logout', route: '/Logout' },
      ],
    },
  ];

  const renderNestedMenu = (parentItem) => (
    <Menu
      anchorEl={nestedAnchorEl}
      open={activeNestedMenu === parentItem.label}
      onClose={handleNestedMenuClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          background: 'linear-gradient(160deg, #3b2410 0%, #5c3d1e 100%)',
          color: 'white',
          minWidth: '220px',
          maxHeight: '70vh',
          overflowY: 'auto',
          ml: 0.5,
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          border: '1px solid rgba(201,168,76,0.3)',
        },
      }}
    >
      {parentItem.subItems?.map((subItem, idx) => (
        subItem.isDivider ? (
          <Box key={idx} sx={{ my: 0.5 }}>
            <Divider sx={{ borderColor: '#c9a84c', borderWidth: '1px' }} />
            <Typography sx={{ color: '#c9a84c', px: 2, py: 0.5, fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {subItem.label}
            </Typography>
          </Box>
        ) : (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(subItem.route)}
            sx={{
              fontSize: '13px', py: 0.75,
              backgroundColor: location.pathname === subItem.route ? 'rgba(201,168,76,0.2) !important' : 'transparent',
              borderLeft: location.pathname === subItem.route ? '3px solid #c9a84c' : '3px solid transparent',
              color: location.pathname === subItem.route ? '#c9a84c' : 'rgba(255,255,255,0.9)',
              fontWeight: location.pathname === subItem.route ? 700 : 400,
              '&:hover': { backgroundColor: 'rgba(201,168,76,0.1) !important' },
            }}
          >
            {subItem.icon && (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', opacity: 0.85 }}>
                {subItem.icon}
              </Box>
            )}
            {subItem.label}
          </MenuItem>
        )
      ))}
    </Menu>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '48px', gap: 0 }}>
      {menuItems.map((item) => {
        if (!item.permission) return null;

        if (item.route && !item.subItems) {
          return (
            <Box
              key={item.id}
              component={NavLink}
              to={item.route}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1,
                color: 'rgba(255,255,255,0.9)', textDecoration: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: 500, height: '100%',
                '&:hover': { backgroundColor: 'rgba(201,168,76,0.15)' },
                '&.active': {
                  backgroundColor: 'rgba(201,168,76,0.2)',
                  borderBottom: '2px solid #c9a84c',
                },
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.label}</Typography>
            </Box>
          );
        }

        const active = isMenuActive(item);

        return (
          <Box key={item.id}>
            <Box
              onClick={(e) => handleMenuOpen(e, item.id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1,
                color: 'rgba(255,255,255,0.9)', cursor: 'pointer', fontSize: '13px', fontWeight: 500, height: '48px',
                borderBottom: active ? '2px solid #c9a84c' : '2px solid transparent',
                backgroundColor: active || activeMenu === item.id ? 'rgba(201,168,76,0.15)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(201,168,76,0.12)' },
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.label}</Typography>
              <KeyboardArrowDown sx={{ fontSize: 16, ml: 0.5 }} />
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={activeMenu === item.id}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  background: 'linear-gradient(160deg, #3b2410 0%, #5c3d1e 100%)',
                  color: 'white', minWidth: '200px',
                  mt: 0, borderRadius: '0 0 8px 8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  border: '1px solid rgba(201,168,76,0.25)',
                  borderTop: 'none',
                },
              }}
            >
              {item.subItems?.map((subItem, idx) => (
                subItem.isNested ? (
                  <Box key={idx}>
                    <MenuItem
                      onClick={(e) => handleNestedMenuOpen(e, subItem.label)}
                      sx={{
                        fontSize: '13px', py: 1, display: 'flex', justifyContent: 'space-between',
                        backgroundColor: subItem.subItems?.some(s => location.pathname === s.route) ? 'rgba(201,168,76,0.15) !important' : 'transparent',
                        borderLeft: subItem.subItems?.some(s => location.pathname === s.route) ? '3px solid #c9a84c' : '3px solid transparent',
                        color: subItem.subItems?.some(s => location.pathname === s.route) ? '#c9a84c' : 'rgba(255,255,255,0.9)',
                        fontWeight: subItem.subItems?.some(s => location.pathname === s.route) ? 700 : 400,
                        '&:hover': { backgroundColor: 'rgba(201,168,76,0.1) !important' },
                        ...(activeNestedMenu === subItem.label && { backgroundColor: 'rgba(201,168,76,0.1) !important' }),
                      }}
                    >
                      {subItem.label}
                      <KeyboardArrowRight sx={{ fontSize: 16 }} />
                    </MenuItem>
                    {renderNestedMenu(subItem)}
                  </Box>
                ) : (
                  <MenuItem
                    key={idx}
                    onClick={() => handleNavigation(subItem.route)}
                    sx={{
                      fontSize: '13px', py: 1,
                      backgroundColor: location.pathname === subItem.route ? 'rgba(201,168,76,0.2) !important' : 'transparent',
                      borderLeft: location.pathname === subItem.route ? '3px solid #c9a84c' : '3px solid transparent',
                      color: location.pathname === subItem.route ? '#c9a84c' : 'rgba(255,255,255,0.9)',
                      fontWeight: location.pathname === subItem.route ? 700 : 400,
                      '&:hover': { backgroundColor: 'rgba(201,168,76,0.1) !important' },
                    }}
                  >
                    {subItem.label}
                  </MenuItem>
                )
              ))}
            </Menu>
          </Box>
        );
      })}
    </Box>
  );
};

export default HorizontalNavbar;
